import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ShopAroundListHeaderBlockConfig } from '@front-platform/content-builder';
import { shopAroundListHeaderBlockResolveConfig } from '@front-platform/content-builder';
import { Router } from '@angular/router';
import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  effect,
  input,
} from '@angular/core';

import { DoodFilterModel } from '@core/models/filters.model';
import { DoodEntityCategory } from '@core/models/entity.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';

import { Paths } from '@config/paths.config';
import { FilterStoreSelector } from '@common/selectors/filter.selector';
import { FilterStoreDispatcher } from '@common/dispatchers/filter.dispatcher';
import { FiltersModalComponent } from '@shared/modals/filters-modal/filters-modal.component';

import { ModalsService } from '@core/services/modals/modals.service';
import { HeaderService } from '@core/services/header/header.service';
import { FiltersService } from '@core/services/filters/filters.service';
import { RouterHelperService } from 'src/app/core/services/router-helper/router-helper.service';
import { AnalyticsService } from '@core/services/app/analytics.service';
export interface ShopAroundListHeaderBlockRuntimeConfig {}

export type ShopAroundListHeaderBlockConfigInput = Partial<
  Omit<ShopAroundListHeaderBlockConfig, keyof ShopAroundListHeaderBlockRuntimeConfig> &
    ShopAroundListHeaderBlockRuntimeConfig
>;
export type ShopAroundListHeaderBlockResolvedConfig = Omit<
  ShopAroundListHeaderBlockConfig,
  keyof ShopAroundListHeaderBlockRuntimeConfig
> &
  Partial<ShopAroundListHeaderBlockRuntimeConfig>;
export interface ShopAroundListHeaderBlockContract extends ConfigComponentContract<
  ShopAroundListHeaderBlockConfigInput,
  ShopAroundListHeaderBlockResolvedConfig
> {}

@Component({
  selector: 'app-shop-around-list-header-block',
  templateUrl: './shop-around-list-header-block.component.html',
  standalone: false,
})
export class ShopAroundListHeaderBlockComponent
  implements AfterContentInit, OnDestroy, ShopAroundListHeaderBlockContract
{
  readonly config = input<ShopAroundListHeaderBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ShopAroundListHeaderBlockConfig,
    ShopAroundListHeaderBlockRuntimeConfig
  >(shopAroundListHeaderBlockResolveConfig, () => ({ config: this.config() }));
  get title(): string {
    return this.resolvedConfig()['title'] as unknown as string;
  }
  get displayTitle(): boolean {
    return this.resolvedConfig()['display_title'] as unknown as boolean;
  }
  get displayAddressSearchInput(): boolean {
    return this.resolvedConfig()['display_address_search_input'] as unknown as boolean;
  }
  get displayFiltersButton(): boolean {
    return this.resolvedConfig()['display_filters_button'] as unknown as boolean;
  }
  get displayMapButton(): boolean {
    return this.resolvedConfig()['display_map_button'] as unknown as boolean;
  }
  get displayMapButtonOnMobile(): boolean {
    return this.resolvedConfig()['display_map_button_on_mobile'] as unknown as boolean;
  }
  get shopsMapButtonColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shops_map_button_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopsMapButtonTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shops_map_button_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopSearchLocationFieldBackgroundColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_search_location_field_background_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopSearchLocationFieldTextColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_search_location_field_text_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopSearchLocationFieldPlaceholderColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_search_location_field_placeholder_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }
  get shopSearchLocationFieldIconColor(): IContentBuilderFieldColor | undefined {
    return this.resolvedConfig()['shop_search_location_field_icon_color'] as unknown as
      IContentBuilderFieldColor | undefined;
  }

  nbFilters$ = this.filterSelector.selectActiveFiltersLength;
  selectedFilters$ = this.filterSelector.selectActiveFilters;
  selectedCategories$ = this.filterSelector.selectCategories;

  constructor(
    private readonly router: Router,
    private filterSelector: FilterStoreSelector,
    private readonly modalsService: ModalsService,
    private readonly headerService: HeaderService,
    private filterDispatcher: FilterStoreDispatcher,
    private readonly filtersService: FiltersService,
    private readonly routerHelper: RouterHelperService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly analyticsService: AnalyticsService,
  ) {
    // Keep the app header in sync when CB2 preview soft-refresh updates block
    // config in place (ngAfterContentInit only runs once per component instance).
    effect(() => {
      const config = this.resolvedConfig();
      const showTitle = !!config['display_title'];
      const title = config['title'] as string | undefined;
      this.headerService.setTitle(showTitle ? title : undefined);
      this.headerService.toggleShopSearchAddressVisibility(
        !!config['display_address_search_input'],
      );
    });
  }

  ngAfterContentInit(): void {
    this.changeDetectorRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.headerService.toggleShopSearchAddressVisibility(false);
    this.headerService.setTitle(undefined);
  }

  openFilters(): void {
    this.modalsService.open(FiltersModalComponent.handle);
  }

  removeFilter(
    filter: DoodFilterModel,
    value?: {
      id?: string | undefined;
      value: string;
    },
  ): void {
    this.filtersService.removeFilter(filter, value?.id);
  }

  removeCategoryFilter(categoryValue: {
    id?: string | undefined;
    value: DoodEntityCategory;
  }): void {
    if (!categoryValue.id) {
      return;
    }
    this.filterDispatcher.removeCategory(categoryValue.id);
  }

  navigateToMapPage(): void {
    this.router.navigate([this.routerHelper.translateRoute(`${Paths.ShopsMap}`)]);
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopAroundListHeaderBlockComponent, {
  'keyAliases': {},
});
